import { useCallback, useEffect, type PropsWithChildren } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChatDialogResponse } from '@/core/generated/api';
import { useAuth } from '@/entities/Auth/store';
import { useChatDialogStore } from '@/entities/ChatDialog/model/store';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { ChatDialogRealtimeContext, useChatDialogRealtime } from '../model/useChatDialogRealtime';
import { useChatDialogNotifications } from './useChatDialogNotifications';

/** Поддерживает обновления и уведомления о переписке на всех страницах админки */
export const ChatDialogRealtimeProvider = ({ children }: PropsWithChildren) => {
    const canView = useCanAccessPermission('admin.chat-dialogs.view');
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const reset = useChatDialogStore((state) => state.reset);
    const navigate = useNavigate();

    useEffect(() => () => reset(), [canView, sessionRevision, reset]);

    /** Открывает переписку из уведомления через обычную навигацию приложения */
    const openDialog = useCallback((dialog: ChatDialogResponse): void => {
        if (!dialog.id) return;

        void navigate({
            pathname: '/chat-dialogs',
            search: new URLSearchParams({ dialogId: dialog.id }).toString(),
        });
    }, [navigate]);

    const notifyIncoming = useChatDialogNotifications(canView, openDialog);
    const realtime = useChatDialogRealtime(canView, notifyIncoming);

    return (
        <ChatDialogRealtimeContext.Provider value={realtime}>
            {children}
        </ChatDialogRealtimeContext.Provider>
    );
};
